import { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  FaHistory, FaEye, FaFilter, FaTimes, FaUser,
  FaCalendarAlt, FaNetworkWired, FaLayerGroup,
} from 'react-icons/fa';
import DataTable from '../../components_shared/DataTable';
import { auditAPI } from '../../utils/api';
import Swal from 'sweetalert2';

const ACTION_META = {
  CREATE: { color: '#10ac84', bg: 'rgba(16,172,132,0.1)',  label: 'CREATE' },
  UPDATE: { color: '#0f6fad', bg: 'rgba(15,111,173,0.1)',  label: 'UPDATE' },
  DELETE: { color: '#e74c3c', bg: 'rgba(231,76,60,0.1)',   label: 'DELETE' },
  VIEW:   { color: '#f39c12', bg: 'rgba(243,156,18,0.1)',  label: 'VIEW'   },
  LOGIN:  { color: '#8e44ad', bg: 'rgba(142,68,173,0.1)',  label: 'LOGIN'  },
  LOGOUT: { color: '#636e72', bg: 'rgba(99,110,114,0.1)',  label: 'LOGOUT' },
};

function ActionBadge({ action }) {
  const meta = ACTION_META[(action || '').toUpperCase()] || { color: '#636e72', bg: 'rgba(99,110,114,0.1)', label: action || '—' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: meta.bg, color: meta.color,
      padding: '3px 10px', borderRadius: 20,
      fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.4px',
    }}>
      {meta.label}
    </span>
  );
}

function formatTs(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-PK', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function AuditLogsTable() {
  const [data, setData]                   = useState([]);
  const [loading, setLoading]             = useState(false);
  const [page, setPage]                   = useState(0);
  const [pageSize, setPageSize]           = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedLog, setSelectedLog]     = useState(null);
  const [viewModal, setViewModal]         = useState(false);

  // Filters
  const [usernameFilter, setUsernameFilter] = useState('');
  const [actionFilter, setActionFilter]     = useState('');
  const [entityFilter, setEntityFilter]     = useState('');
  const [startDate, setStartDate]           = useState('');
  const [endDate, setEndDate]               = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        size: pageSize,
        ...(usernameFilter && { username: usernameFilter }),
        ...(actionFilter   && { action:   actionFilter }),
        ...(entityFilter   && { entity:   entityFilter }),
        ...(startDate      && { start:    startDate }),
        ...(endDate        && { end:      `${endDate}T23:59:59` }),
      };
      const res = await auditAPI.getLogs(params);
      setData(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
    } catch (err) {
      Swal.fire({ title: 'Error', text: err.message, icon: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, usernameFilter, actionFilter, entityFilter, startDate, endDate]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const clearFilters = () => {
    setUsernameFilter('');
    setActionFilter('');
    setEntityFilter('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const hasFilters = usernameFilter || actionFilter || entityFilter || startDate || endDate;

  const columns = [
    {
      accessorKey: 'username',
      header: 'User',
      cell: ({ getValue }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'rgba(15,111,173,0.1)', color: '#0f6fad',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.75rem', flexShrink: 0,
          }}>
            <FaUser />
          </div>
          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{getValue() || '—'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'action',
      header: 'Action',
      cell: ({ getValue }) => <ActionBadge action={getValue()} />,
    },
    {
      accessorKey: 'entity',
      header: 'Entity',
      cell: ({ getValue }) => (
        <span style={{
          background: '#f1f5f9', color: '#374151',
          padding: '3px 10px', borderRadius: 6,
          fontSize: '0.78rem', fontWeight: 600,
        }}>
          {getValue() || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'details',
      header: 'Details',
      cell: ({ getValue }) => {
        const v = getValue();
        if (!v) return <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontSize: '0.78rem' }}>—</span>;
        return (
          <span style={{ fontSize: '0.8rem', color: '#475569', maxWidth: 220, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={v}>
            {v}
          </span>
        );
      },
    },
    {
      accessorKey: 'ip_address',
      header: 'IP',
      cell: ({ getValue }) => (
        <span style={{ fontSize: '0.78rem', color: '#64748b', fontFamily: 'monospace' }}>
          {getValue() || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'timestamp',
      header: 'Timestamp',
      cell: ({ getValue }) => (
        <span style={{ fontSize: '0.82rem', color: '#374151' }}>{formatTs(getValue())}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <button
          title="View details"
          onClick={() => { setSelectedLog(row.original); setViewModal(true); }}
          style={{
            width: 30, height: 30, borderRadius: 7,
            background: 'rgba(16,172,132,0.08)', border: '1px solid rgba(16,172,132,0.15)',
            color: '#10ac84', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.8rem', padding: 0,
          }}
        >
          <FaEye />
        </button>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Stats row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard value={totalElements} label="Total Log Entries"   color="#0f6fad" />
        <StatCard value={data.filter(l => (l.action||'').toUpperCase() === 'CREATE').length} label="Creates (this page)" color="#10ac84" />
        <StatCard value={data.filter(l => (l.action||'').toUpperCase() === 'UPDATE').length} label="Updates (this page)" color="#0f6fad" />
        <StatCard value={data.filter(l => (l.action||'').toUpperCase() === 'DELETE').length} label="Deletes (this page)" color="#e74c3c" />
      </div>

      {/* Main card */}
      <div style={cardStyle}>
        {/* Card header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '22px 28px 0', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(15,111,173,0.1)', color: '#0f6fad', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
              <FaHistory />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>Audit Logs</h2>
              <p style={{ margin: '3px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>Track all administrative actions and system changes</p>
            </div>
          </div>
          {hasFilters && (
            <button onClick={clearFilters} style={clearBtn}>
              <FaTimes /> Clear Filters
            </button>
          )}
        </div>

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 10, padding: '16px 28px', borderBottom: '1px solid #f0f4f8', flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterInput
            icon={<FaUser />}
            placeholder="Filter by user..."
            value={usernameFilter}
            onChange={e => { setUsernameFilter(e.target.value); setPage(0); }}
            onClear={() => { setUsernameFilter(''); setPage(0); }}
          />
          <FilterInput
            icon={<FaLayerGroup />}
            placeholder="Filter by entity..."
            value={entityFilter}
            onChange={e => { setEntityFilter(e.target.value); setPage(0); }}
            onClear={() => { setEntityFilter(''); setPage(0); }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaFilter style={{ color: '#94a3b8', fontSize: '0.75rem' }} />
            <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0); }} style={selectStyle}>
              <option value="">All Actions</option>
              {Object.keys(ACTION_META).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaCalendarAlt style={{ color: '#94a3b8', fontSize: '0.75rem' }} />
            <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(0); }} style={{ ...selectStyle, minWidth: 140 }} />
            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>to</span>
            <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(0); }} style={{ ...selectStyle, minWidth: 140 }} />
          </div>
        </div>

        <div style={{ padding: '0 28px 24px' }}>
          <DataTable
            data={data} columns={columns} loading={loading}
            serverSide totalElements={totalElements}
            page={page} pageSize={pageSize}
            onPageChange={setPage} onPageSizeChange={setPageSize}
          />
        </div>
      </div>

      {/* Detail modal — portalled to body so sidebar offset doesn't affect centering */}
      {viewModal && selectedLog && ReactDOM.createPortal(
        <div onClick={() => setViewModal(false)} style={overlayStyle}>
          <div onClick={e => e.stopPropagation()} style={modalStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f0f4f8' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <ActionBadge action={selectedLog.action} />
                <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>Log Detail</span>
              </div>
              <button onClick={() => setViewModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.1rem', padding: 4 }}>
                <FaTimes />
              </button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <InfoCell icon={<FaUser />}         label="User"       value={selectedLog.username} />
                <InfoCell icon={<FaLayerGroup />}   label="Entity"     value={selectedLog.entity} />
                <InfoCell icon={<FaCalendarAlt />}  label="Timestamp"  value={formatTs(selectedLog.timestamp)} />
                <InfoCell icon={<FaNetworkWired />} label="IP Address" value={selectedLog.ip_address} />
              </div>

              {selectedLog.details && (
                <Section label="Details">
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', fontSize: '0.875rem', color: '#374151', lineHeight: 1.6 }}>
                    {selectedLog.details}
                  </div>
                </Section>
              )}

              {selectedLog.old_values && Object.keys(selectedLog.old_values).length > 0 && (
                <Section label="Before (old values)">
                  <pre style={{ ...preStyle, borderLeft: '3px solid #e74c3c' }}>
                    {JSON.stringify(selectedLog.old_values, null, 2)}
                  </pre>
                </Section>
              )}

              {selectedLog.new_values && Object.keys(selectedLog.new_values).length > 0 && (
                <Section label="After (new values)">
                  <pre style={{ ...preStyle, borderLeft: '3px solid #10ac84' }}>
                    {JSON.stringify(selectedLog.new_values, null, 2)}
                  </pre>
                </Section>
              )}

              {selectedLog.user_agent && (
                <Section label="User Agent">
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', wordBreak: 'break-all', lineHeight: 1.5 }}>
                    {selectedLog.user_agent}
                  </div>
                </Section>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ---- Helpers ---- */
function StatCard({ value, label, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #f0f4f8', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: '1.3rem', fontWeight: 700, flexShrink: 0 }}>{value}</div>
      <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 500 }}>{label}</div>
    </div>
  );
}

function FilterInput({ icon, placeholder, value, onChange, onClear }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '0 12px', height: 40, minWidth: 180 }}>
      <span style={{ color: '#94a3b8', fontSize: '0.8rem', marginRight: 8, flexShrink: 0 }}>{icon}</span>
      <input type="text" value={value} onChange={onChange} placeholder={placeholder}
        style={{ flex: 1, border: 'none', background: 'transparent', fontSize: '0.875rem', color: '#374151', outline: 'none', fontFamily: "'Poppins',sans-serif" }}
      />
      {value && (
        <button onClick={onClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex', alignItems: 'center' }}>
          <FaTimes style={{ fontSize: '0.72rem' }} />
        </button>
      )}
    </div>
  );
}

function InfoCell({ icon, label, value }) {
  return (
    <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{icon}</span>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
      </div>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', wordBreak: 'break-all' }}>{value || '—'}</div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>{label}</div>
      {children}
    </div>
  );
}

const cardStyle    = { background: '#fff', borderRadius: 14, border: '1px solid #f0f4f8', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' };
const selectStyle  = { padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '0.875rem', background: '#f8fafc', color: '#374151', fontFamily: "'Poppins',sans-serif", cursor: 'pointer', height: 40 };
const clearBtn     = { display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(231,76,60,0.08)', color: '#e74c3c', border: '1px solid rgba(231,76,60,0.15)', borderRadius: 10, padding: '8px 14px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins',sans-serif" };
const overlayStyle = { position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 };
const modalStyle   = { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const preStyle     = { background: '#1e293b', color: '#e2e8f0', borderRadius: 10, padding: '12px 16px', fontSize: '0.78rem', overflowX: 'auto', maxHeight: 200, margin: 0, lineHeight: 1.6 };
